import { baseApi } from "./baseApi";
import {
  clearStoredClientUser,
  getStoredClientUser,
  saveStoredClientUser,
  subscribeToClientAuth,
} from "../auth/clientSession";
import type { AuthResponse, ClientSession, LoginPayload } from "../types/common";

const toTrimmedString = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const normalizeClientSession = (response: AuthResponse): ClientSession => ({
  token: toTrimmedString(response.accessToken),
  tokenExpires: toTrimmedString(response.expiration) || undefined,
  id: String(response.user.id ?? response.user.email ?? "client"),
  name: toTrimmedString(response.user.name) || "Client User",
  email: toTrimmedString(response.user.email).toLowerCase(),
  role: toTrimmedString(response.user.role) || "Client",
  phone: toTrimmedString(response.user.phone),
  city: toTrimmedString(response.user.city) || undefined,
});

export { getStoredClientUser, saveStoredClientUser, subscribeToClientAuth };
export type { ClientSession };

export const logoutClient = () => {
  clearStoredClientUser();
};

export const clientAuthApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    loginClient: builder.mutation<ClientSession, LoginPayload>({
      query: ({ email, password }) => ({
        url: "/Auth/client-login",
        method: "POST",
        body: {
          username: email.trim(),
          password,
        },
      }),
      transformResponse: normalizeClientSession,
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data: clientSession } = await queryFulfilled;
          saveStoredClientUser(clientSession);
        } catch {
          // Error will be handled by the component
        }
      },
      invalidatesTags: ["ClientAuth"],
    }),
    logoutClient: builder.mutation<void, void>({
      query: () => ({
        url: "/Auth/logout",
        method: "POST",
      }),
      async onQueryStarted(_args, _api) {
        clearStoredClientUser();
      },
      invalidatesTags: ["ClientAuth"],
    }),
  }),
});

export const { useLoginClientMutation, useLogoutClientMutation } = clientAuthApi;
